export type GLMCardTypes =
  | 'trigger'
  | 'action'
  | 'package'
  | 'status'
  | 'enabled'
  | 'tag'
  | 'num_checkins'
  | 'pipeline_stage'
  | 'appointment_type'
  | 'class_type'
  | null;

export enum KiloGLMFlowTriggers {
  ATHLETE_CREATED = "Athlete created",
  ATHLETE_DATA_UPDATED = "Athlete data updated",
  PACKAGE_STARTED = "Package started",
  PACKAGE_PURCHASED = "Package purchased",
  PACKAGE_ENDED = "Package ended",
  PACKAGE_CANCELLED = "Package cancelled",
  PACKAGE_HOLD_START = "Package hold started",
  PACKAGE_HOLD_END = "Package hold ended",
  ATHLETE_STATUS_CHANGED = "Athlete status changed",
  ATHLETE_CHECKINS_REACHED = "Athlete checkins reached",
  CLASS_CHECKIN = "Class check-in",
  APPPOINTMENT_CHECKIN = "Appointment check-in",
  NOTE_CREATED_OR_EDITED = "Note created or edited",
  NOTE_DELETED = "Note deleted",
}

export enum KiloGLMFlowActions {
  ADD_TAGS = 'add_tags',
  REMOVE_TAGS = 'remove_tags',
  UPSERT_NOTE = 'upsert_note',
  DELETE_NOTE = 'delete_note',
  LINK_CONTACT_AND_ADD_TAG = 'link_contact_and_add_tag',
  LINK_CONTACT_AND_MOVE_PIPELINE_STAGE = 'link_contact_and_move_pipeline_stage',
  SYNC_CONTACT_DATA = 'sync_contact_data',
}

export const TRIGGER_LABELS: Record<KiloGLMFlowTriggers, string> = {
  [KiloGLMFlowTriggers.ATHLETE_CREATED]: 'Athlete Created',
  [KiloGLMFlowTriggers.ATHLETE_DATA_UPDATED]: "An athlete's data is updated",
  [KiloGLMFlowTriggers.PACKAGE_STARTED]: 'Package Started',
  [KiloGLMFlowTriggers.PACKAGE_PURCHASED]: 'Package Purchased',
  [KiloGLMFlowTriggers.PACKAGE_ENDED]: 'Package Ended',
  [KiloGLMFlowTriggers.PACKAGE_CANCELLED]: 'Package Cancelled Early',
  [KiloGLMFlowTriggers.PACKAGE_HOLD_START]: 'Package Hold Start',
  [KiloGLMFlowTriggers.PACKAGE_HOLD_END]: 'Package Hold End',
  [KiloGLMFlowTriggers.ATHLETE_STATUS_CHANGED]: 'Athlete Status Changed',
  [KiloGLMFlowTriggers.ATHLETE_CHECKINS_REACHED]: 'Check-in Count Reached',
  [KiloGLMFlowTriggers.CLASS_CHECKIN]: 'Class Check-in',
  [KiloGLMFlowTriggers.APPPOINTMENT_CHECKIN]: 'Appointment Check-in',
  [KiloGLMFlowTriggers.NOTE_CREATED_OR_EDITED]: 'A note is created or edited',
  [KiloGLMFlowTriggers.NOTE_DELETED]: 'A note is deleted',
};

export const TRIGGER_DESCRIPTIONS: Record<KiloGLMFlowTriggers, string> = {
  [KiloGLMFlowTriggers.ATHLETE_CREATED]:
    'Fires when a new athlete is created in Kilo. Does not fire for dependants or linked athletes.',
  [KiloGLMFlowTriggers.ATHLETE_DATA_UPDATED]:
    "When an athlete's profile data changes in Kilo, syncs the changed fields to the linked GLM contact. Email is never synced.",
  [KiloGLMFlowTriggers.PACKAGE_STARTED]: "Fires on the start date of an athlete's package.",
  [KiloGLMFlowTriggers.PACKAGE_PURCHASED]:
    'Triggers when an athlete purchases a package in Kilo, before the package start date.',
  [KiloGLMFlowTriggers.PACKAGE_ENDED]: "Fires when an athlete's package reaches its end date.",
  [KiloGLMFlowTriggers.PACKAGE_CANCELLED]:
    'Fires when a package is cancelled before its planned end date.',
  [KiloGLMFlowTriggers.PACKAGE_HOLD_START]: "Fires when an athlete's package is placed on hold.",
  [KiloGLMFlowTriggers.PACKAGE_HOLD_END]: "Fires when an athlete's package hold is removed or ends.",
  [KiloGLMFlowTriggers.ATHLETE_STATUS_CHANGED]:
    "Fires when any change is made to an athlete's current status in Kilo.",
  [KiloGLMFlowTriggers.ATHLETE_CHECKINS_REACHED]:
    'Fires when an athlete reaches a defined total number of check-ins (class or appointment).',
  [KiloGLMFlowTriggers.CLASS_CHECKIN]: 'Fires each time an athlete checks into a class.',
  [KiloGLMFlowTriggers.APPPOINTMENT_CHECKIN]:
    'Fires each time an athlete checks into an appointment.',
  [KiloGLMFlowTriggers.NOTE_CREATED_OR_EDITED]:
    'When a note is created or edited on an athlete in Kilo, mirrors it onto the linked GLM contact.',
  [KiloGLMFlowTriggers.NOTE_DELETED]:
    'When a note is deleted from an athlete in Kilo, removes the mirrored note from the linked GLM contact.',
};

export const ACTION_LABELS: Record<KiloGLMFlowActions, string> = {
  [KiloGLMFlowActions.ADD_TAGS]: 'Add tag to contact',
  [KiloGLMFlowActions.REMOVE_TAGS]: 'Remove tag from contact',
  [KiloGLMFlowActions.UPSERT_NOTE]: 'Create or update note for contact',
  [KiloGLMFlowActions.DELETE_NOTE]: 'Delete note for contact',
  [KiloGLMFlowActions.LINK_CONTACT_AND_ADD_TAG]: 'Link contact and add tag',
  [KiloGLMFlowActions.LINK_CONTACT_AND_MOVE_PIPELINE_STAGE]: 'Link contact and move pipeline stage',
  [KiloGLMFlowActions.SYNC_CONTACT_DATA]: 'Sync contact data',
};

export interface FormValues {
  kiloGlmFlowId: string;
  trigger: KiloGLMFlowTriggers | '';
  action: KiloGLMFlowActions | '';
  enabled: boolean;
  // Internal form fields. normalizePayload maps these to the KiloGLMFlowWrite
  // schema: tags→acTags, athleteStatus→tcAthleteStatus, checkinsNumber→tcCheckinsNumber.
  tags: string[] | null;
  acPipelineId: string | null;
  acStageId: string | null;
  athleteStatus: string | null;
  checkinsNumber: number | null;
  packageIds: string[] | null;
  classTypeIds: string[] | null;
  appointmentTypeIds: string[] | null;
  // id → name maps carried alongside the id lists so the builder cards can render
  // the selected names directly, without re-resolving against a capped autocomplete page.
  packageLabels: Record<string, string>;
  appointmentTypeLabels: Record<string, string>;
  classTypeLabels: Record<string, string>;
}
